/**
 * 認証関連のAPI呼び出し専用クライアント
 * パスワードリセット、ログイン、ログアウト機能を実装
 */

import BaseApiClient from './baseApiClient.js';

class AuthApiClient extends BaseApiClient {
  constructor(config = {}) {
    super(config);
    
    if (this.baseUrl === null || this.baseUrl === undefined) {
      throw new Error('API_BASE_URL is not configured.');
    }
  }

  /**
   * 認証コード送信API呼び出し
   */
  async sendVerificationCode(email) {
    const result = await this.post(this.endpoints.AUTH.SEND_VERIFICATION, { email });
    
    if (result.ok) {
      return result.data?.success ?? true;
    }
    
    // バックエンドから返ってきたエラーメッセージを使用
    const errorMessage = result.data?.message || `Failed to send verification code: ${result.status}`;
    throw new Error(errorMessage);
  }

  /**
   * 認証コード検証API呼び出し
   */
  async verifyCode(email, code) {
    const result = await this.post(this.endpoints.AUTH.VERIFY_CODE, { email, code });
    
    if (result.ok) {
      return result.data?.success ?? true;
    }
    
    // バックエンドから返ってきたエラーメッセージを使用
    const errorMessage = result.data?.message || `Failed to verify code: ${result.status}`;
    throw new Error(errorMessage);
  }

  /**
   * 認証コード再送信API呼び出し
   */
  async resendVerificationCode(email) {
    return this.sendVerificationCode(email);
  }

  /**
   * パスワードリセットメール送信API呼び出し
   * @param {string} email - メールアドレス
   * @returns {Promise<Object>} 送信結果
   */
  async sendPasswordResetEmail(email) {
    try {
      
      const result = await this.post(this.endpoints.AUTH.PASSWORD_RESET, { email });
      
      if (result.ok) {
        return {
          success: true,
          message: result.data?.message || 'パスワードリセット用のメールを送信しました'
        };
      }
      
      // エラーレスポンス
      const errorMessage = result.data?.message || 'メール送信に失敗しました';
      console.error('[AUTH API CLIENT] Password reset failed:', errorMessage);
      
      return {
        success: false,
        error: errorMessage
      };
      
    } catch (error) {
      console.error('[AUTH API CLIENT] Password reset request failed:', error);
      throw new Error(`パスワードリセットに失敗しました: ${error.message}`);
    }
  }

  /**
   * ログインAPI呼び出し
   * @param {string} email - メールアドレス
   * @param {string} password - パスワード
   * @returns {Promise<Object>} ログイン結果
   */
  async login(email, password) {
    try {
      
      const result = await this.post(this.endpoints.AUTH.LOGIN, { email, password });
      
      if (result.ok && result.data) {
        return {
          success: true,
          memberId: result.data.memberId,
          firebaseUid: result.data.firebaseUid,
          memberInfo: result.data.memberInfo,
          message: result.data.message
        };
      }
      
      // ログイン失敗
      const errorMessage = result.data?.message || 'ログインに失敗しました';
      console.error('[AUTH API CLIENT] Login failed:', errorMessage);
      
      return {
        success: false,
        error: errorMessage
      };
      
    } catch (error) {
      console.error('[AUTH API CLIENT] Login request failed:', error);
      throw new Error(`ログインに失敗しました: ${error.message}`);
    }
  }

  /**
   * ログアウトAPI呼び出し
   * @returns {Promise<Object>} ログアウト結果
   */
  async logout() {
    try {
      const result = await this.post(this.endpoints.AUTH.LOGOUT, null, {
        credentials: 'include'
      });
      
      if (result.ok && result.data) {
        return {
          success: true,
          message: result.data.message,
          redirectUrl: result.data.redirectUrl
        };
      }
      
      // ログアウト失敗（稀なケース）
      console.warn('[AUTH API CLIENT] Logout failed:', result);
      
      return {
        success: false,
        error: result.data?.message || 'ログアウトに失敗しました'
      };
      
    } catch (error) {
      console.error('[AUTH API CLIENT] Logout request failed:', error);
      // ログアウトはエラーでも成功扱い
      return {
        success: true,
        message: 'ログアウトしました',
        redirectUrl: '/login'
      };
    }
  }
}

// デフォルトインスタンス
export const authApiClient = new AuthApiClient();
export default AuthApiClient;